/**
 * The book's MESSAGES page as data: notes, calls, offers, warnings — newest first, read / unread, with an
 * optional action (mark a place on the map, open an online panel). Pure (no DOM, no engine) and
 * serialisable, so the offline inbox survives a reload. Unit-tested in book.test.ts.
 */

export type MessageKind =
  /** Notes from Mags and the story's contacts. */
  | 'story'
  /** A call taken at a ringing phone box, written down afterwards. */
  | 'call'
  /** A job going (export orders, new jobs). */
  | 'offer'
  /** Raids, crackdowns, the law sniffing about. */
  | 'warning'
  /** Police tips and useful advice from contacts. */
  | 'tip'
  | 'crew'
  | 'gang'
  /** Somebody said your name in chat. */
  | 'chat'
  | 'system';

export interface MessageAction {
  label: string;
  /** Put a waypoint on the map. */
  mark?: { x: number; z: number; label: string };
  /** Open an online panel (OnlineGame.open). */
  panel?: string;
  panelArg?: unknown;
  /** Turn to another page of the book. */
  tab?: string;
}

export interface BookMessage {
  id: string;
  from: string;
  subject: string;
  text: string;
  kind: MessageKind;
  /** Wall-clock ms when it arrived (for "5 min ago"); never used by the simulation. */
  at: number;
  read: boolean;
  action?: MessageAction;
}

export interface PostInput {
  from: string;
  subject: string;
  text: string;
  kind: MessageKind;
  action?: MessageAction;
  /** De-duplication key: a message with a key already seen is dropped (even after it was trimmed away). */
  key?: string;
  /** Arrive already read (e.g. the intro note the player just read on screen). */
  read?: boolean;
  /** Arrive unread but without the bleep and its slip (a note the player has just seen on screen). */
  quiet?: boolean;
}

export interface InboxData {
  v: 1;
  seq: number;
  messages: BookMessage[];
  keys: string[];
}

export const INBOX_MAX = 50;
const KEYS_MAX = 400;

const KINDS: readonly MessageKind[] = ['story', 'call', 'offer', 'warning', 'tip', 'crew', 'gang', 'chat', 'system'];

export class Inbox {
  private messages: BookMessage[] = [];
  private keys = new Set<string>();
  private seq = 0;

  constructor(private readonly now: () => number = () => Date.now()) {}

  /** Add a message (newest first). Returns it, or null when its key was seen before. */
  post(m: PostInput): BookMessage | null {
    if (m.key) {
      if (this.keys.has(m.key)) return null;
      this.keys.add(m.key);
      if (this.keys.size > KEYS_MAX) this.keys.delete(this.keys.values().next().value as string);
    }
    const msg: BookMessage = {
      id: `m${++this.seq}`,
      from: m.from,
      subject: m.subject,
      text: m.text,
      kind: m.kind,
      at: this.now(),
      read: !!m.read,
      ...(m.action ? { action: m.action } : {}),
    };
    this.messages.unshift(msg);
    if (this.messages.length > INBOX_MAX) this.messages.length = INBOX_MAX;
    return msg;
  }

  has(key: string): boolean {
    return this.keys.has(key);
  }

  /** Newest first. */
  list(): readonly BookMessage[] {
    return this.messages;
  }

  get unread(): number {
    let n = 0;
    for (const m of this.messages) if (!m.read) n++;
    return n;
  }

  get(id: string): BookMessage | undefined {
    return this.messages.find((m) => m.id === id);
  }

  markRead(id: string): boolean {
    const m = this.get(id);
    if (!m || m.read) return false;
    m.read = true;
    return true;
  }

  markAllRead(): number {
    let n = 0;
    for (const m of this.messages) {
      if (!m.read) {
        m.read = true;
        n++;
      }
    }
    return n;
  }

  clear(): void {
    this.messages = [];
    this.keys.clear();
    this.seq = 0;
  }

  serialize(): InboxData {
    return { v: 1, seq: this.seq, messages: this.messages.map((m) => ({ ...m })), keys: [...this.keys] };
  }

  /** Restore from storage; anything malformed is dropped rather than trusted. */
  restore(data: unknown): void {
    this.clear();
    if (!data || typeof data !== 'object') return;
    const d = data as Partial<InboxData>;
    if (d.v !== 1) return;
    const str = (x: unknown, max = 600): string | null => (typeof x === 'string' ? x.slice(0, max) : null);
    for (const raw of Array.isArray(d.messages) ? d.messages.slice(0, INBOX_MAX) : []) {
      if (!raw || typeof raw !== 'object') continue;
      const r = raw as unknown as Record<string, unknown>;
      const id = str(r.id, 20);
      const from = str(r.from, 60);
      const subject = str(r.subject, 120);
      const text = str(r.text);
      const kind = KINDS.includes(r.kind as MessageKind) ? (r.kind as MessageKind) : null;
      if (!id || from === null || subject === null || text === null || !kind) continue;
      const msg: BookMessage = { id, from, subject, text, kind, at: typeof r.at === 'number' && Number.isFinite(r.at) ? r.at : 0, read: r.read === true };
      const a = r.action as Record<string, unknown> | undefined;
      if (a && typeof a === 'object' && typeof a.label === 'string') {
        const action: MessageAction = { label: a.label.slice(0, 60) };
        const mk = a.mark as Record<string, unknown> | undefined;
        if (mk && typeof mk.x === 'number' && typeof mk.z === 'number' && Number.isFinite(mk.x) && Number.isFinite(mk.z)) {
          action.mark = { x: mk.x, z: mk.z, label: typeof mk.label === 'string' ? mk.label.slice(0, 60) : '' };
        }
        if (typeof a.tab === 'string') action.tab = a.tab.slice(0, 20);
        msg.action = action;
      }
      this.messages.push(msg);
    }
    for (const k of Array.isArray(d.keys) ? d.keys.slice(-KEYS_MAX) : []) if (typeof k === 'string') this.keys.add(k);
    const maxSeq = this.messages.reduce((n, m) => Math.max(n, Number(m.id.slice(1)) || 0), 0);
    this.seq = Math.max(typeof d.seq === 'number' && Number.isFinite(d.seq) ? d.seq : 0, maxSeq);
  }
}

/** "just now", "5 min ago", "2 hr ago", "3 days ago" — the book's margin note next to a message. */
export function agoText(at: number, now: number): string {
  const s = Math.max(0, (now - at) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}
